/* StageDataDialog — generic data-capture dialog for Layer 2 of the
 * production workflow. Renders the right form per stage by reading the
 * `stage_meta_schemas` map returned by GET /production/cases/:id/workflow.
 *
 * Why generic instead of seven dedicated dialogs? Two reasons:
 *
 *  1) The lab SOP will keep evolving — new stages, new fields, new QC
 *     items. With a schema-driven renderer, every change is a single
 *     backend dict edit. No frontend code touches needed.
 *
 *  2) Field types are limited (text · number · date · checkbox · select)
 *     so a 5-case switch handles 100% of today's inputs cleanly.
 *
 * Flow:
 *   - parent (`ProductionWorkflowStepper`) sets `open` + passes `stageSlug`
 *   - we render the schema's fields; on submit we POST /advance with
 *     `meta` filled and the parent refreshes the workflow state
 *
 * The QC stage gets a slightly different visual treatment — it's a
 * formal sign-off so we group the 9 checkboxes under an "Inspection
 * checklist" header and surface the inspector identity at the bottom.
 */
import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, ClipboardText, Printer, ThermometerHot, Scissors, Drop, Package, Warning } from "@phosphor-icons/react";
import PhotoUploadField from "@/components/production/PhotoUploadField";


// ─── Stage hero icons + headings (cosmetic — schema is source of truth)
const STAGE_HERO = {
    digital_prep:        { icon: ClipboardText, title: "Digital file preparation",  desc: "Confirm STL nesting + pre-print verification before committing the build." },
    model_production:    { icon: Printer,       title: "3D printing",                 desc: "Log the printer + resin batch so the audit trail carries traceability." },
    thermoforming:       { icon: ThermometerHot, title: "Thermoforming",              desc: "Capture the machine ID + material batch for every aligner formed." },
    trimming_finishing:  { icon: Scissors,      title: "Trimming & finishing",        desc: "Trim style + polishing + attachment cutouts." },
    cleaning:            { icon: Drop,          title: "Cleaning & sterilization",    desc: "Cleaning method + sterilization step + dry confirmation." },
    qc_review:           { icon: ShieldCheck,   title: "Quality control inspection",  desc: "Mandatory 9-point inspection. Every item must be confirmed before sign-off." },
    packaging:           { icon: Package,       title: "Packaging",                   desc: "Cross-verify the shipping label against the case file — single mismatch = stop." },
};


/* Build a fresh defaults object for a schema. Numbers default to "" (string)
   so the input is controlled; checkboxes default to false; rest empty-string. */
function buildDefaults(fields) {
    const d = {};
    for (const [k, spec] of Object.entries(fields || {})) {
        if (spec.type === "checkbox") d[k] = false;
        else d[k] = "";
    }
    return d;
}


function isFormValid(fields, values) {
    if (!fields) return true;
    for (const [k, spec] of Object.entries(fields)) {
        if (!spec.required) continue;
        const v = values[k];
        if (spec.type === "checkbox") {
            if (v !== true) return false;
        } else if (spec.type === "number") {
            if (v === "" || v === null || v === undefined) return false;
        } else {
            if (!v) return false;
        }
    }
    return true;
}


export default function StageDataDialog({ open, onClose, onSubmit, stageSlug, schemas, busy, caseId }) {
    const fields = schemas?.[stageSlug]?.fields || null;
    const [values, setValues] = useState({});
    const [photoIds, setPhotoIds] = useState([]);  // iter97.17 — per-stage photo evidence

    useEffect(() => {
        if (open && fields) {
            setValues(buildDefaults(fields));
            setPhotoIds([]);
        }
    }, [open, stageSlug]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!fields) {
        // No schema for this stage → caller shouldn't have opened us.
        // Defensive close.
        if (open) onClose?.();
        return null;
    }

    const hero = STAGE_HERO[stageSlug] || { icon: ClipboardText, title: "Stage data", desc: "" };
    const HeroIcon = hero.icon;
    const isQc = stageSlug === "qc_review";
    const valid = isFormValid(fields, values);
    const setField = (k, v) => setValues((p) => ({ ...p, [k]: v }));

    const submit = () => {
        if (!valid || busy) return;
        // Coerce number fields to actual numbers before sending
        const meta = {};
        for (const [k, spec] of Object.entries(fields)) {
            if (spec.type === "number" && values[k] !== "") {
                const n = Number(values[k]);
                meta[k] = Number.isFinite(n) ? n : values[k];
            } else {
                meta[k] = values[k];
            }
        }
        // iter97.17 — bundle photo IDs into meta so backend persists them
        // alongside the stage entry without needing a separate column.
        if (photoIds.length > 0) {
            meta.attachment_file_ids = photoIds;
        }
        onSubmit({ meta });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`stage-data-dialog-${stageSlug}`}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <HeroIcon size={18} weight="duotone" className="text-primary" />
                        {hero.title}
                    </DialogTitle>
                    {hero.desc && <DialogDescription>{hero.desc}</DialogDescription>}
                </DialogHeader>

                {isQc && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs flex items-start gap-2" data-testid="qc-warning-banner">
                        <Warning size={14} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
                        <div>
                            <span className="font-semibold text-amber-900">Anti-self-QC enforced.</span>
                            <span className="text-amber-800/90"> You can't sign off QC on a case you fabricated — the backend will reject the submission with a 403. Pass to a colleague if so.</span>
                        </div>
                    </div>
                )}

                <div className={`${isQc ? "grid grid-cols-1 gap-2" : "space-y-3"} mt-1`}>
                    {Object.entries(fields).map(([k, spec]) => (
                        <FieldRow
                            key={k}
                            slug={k}
                            spec={spec}
                            value={values[k]}
                            onChange={(v) => setField(k, v)}
                            compact={isQc && spec.type === "checkbox"}
                        />
                    ))}
                </div>

                {/* iter97.17 — Per-stage photo evidence. Optional. Auto-attached
                    to the stage history entry as meta.attachment_file_ids. */}
                {caseId && (
                    <div className="pt-2 mt-2 border-t border-border">
                        <PhotoUploadField
                            caseId={caseId}
                            kind={`production_${stageSlug}`}
                            value={photoIds}
                            onChange={setPhotoIds}
                            label="Photo evidence (optional)"
                            helperText={isQc
                                ? "Photos of defects or pass-checks help build the QC audit trail."
                                : "Attach photos of fabricated trays, equipment readings, or unusual findings."}
                            testid={`stage-photos-${stageSlug}`}
                            disabled={busy}
                            max={4}
                        />
                    </div>
                )}

                <DialogFooter className="mt-3">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={!valid || busy}
                        data-testid={`stage-data-submit-${stageSlug}`}
                    >
                        {busy ? "Saving…" : isQc ? "Sign off & advance" : "Save & advance"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─── FieldRow — render a single field based on its type */
function FieldRow({ slug, spec, value, onChange, compact }) {
    const required = spec.required ? <span className="text-rose-600 ml-1">*</span> : null;
    const testId = `stage-field-${slug}`;

    if (spec.type === "checkbox") {
        return (
            <label
                className={`flex items-start gap-2 ${compact ? "p-2" : "p-2.5"} rounded-lg border border-border hover:bg-secondary/20 cursor-pointer`}
                data-testid={testId}
            >
                <Checkbox
                    checked={!!value}
                    onCheckedChange={onChange}
                    className="mt-0.5"
                    data-testid={`${testId}-input`}
                />
                <span className="text-xs leading-snug">
                    {spec.label}{required}
                </span>
            </label>
        );
    }

    if (spec.type === "select") {
        return (
            <div data-testid={testId}>
                <Label className="text-xs font-semibold">{spec.label}{required}</Label>
                <Select value={value || ""} onValueChange={onChange}>
                    <SelectTrigger className="h-9 mt-1 text-sm" data-testid={`${testId}-trigger`}>
                        <SelectValue placeholder="Select…" />
                    </SelectTrigger>
                    <SelectContent>
                        {(spec.options || []).map((opt) => (
                            <SelectItem key={opt} value={opt} data-testid={`${testId}-opt-${opt}`}>
                                {opt}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        );
    }

    if (spec.type === "date") {
        return (
            <div data-testid={testId}>
                <Label className="text-xs font-semibold">{spec.label}{required}</Label>
                <Input
                    type="date"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="h-9 mt-1"
                    data-testid={`${testId}-input`}
                />
            </div>
        );
    }

    if (spec.type === "number") {
        return (
            <div data-testid={testId}>
                <Label className="text-xs font-semibold">{spec.label}{required}</Label>
                <Input
                    type="number"
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={spec.placeholder || ""}
                    className="h-9 mt-1"
                    data-testid={`${testId}-input`}
                />
            </div>
        );
    }

    // Default: text input
    if ((spec.label || "").toLowerCase().includes("notes")) {
        return (
            <div data-testid={testId}>
                <Label className="text-xs font-semibold">{spec.label}{required}</Label>
                <Textarea
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={spec.placeholder || ""}
                    rows={2}
                    className="mt-1"
                    data-testid={`${testId}-input`}
                />
            </div>
        );
    }

    return (
        <div data-testid={testId}>
            <Label className="text-xs font-semibold">{spec.label}{required}</Label>
            <Input
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={spec.placeholder || ""}
                className="h-9 mt-1"
                data-testid={`${testId}-input`}
            />
        </div>
    );
}


/* ─── StageMetaDetails — read-only meta renderer for completed stages
   Used by the stepper to surface lab-grade detail under each passed
   stage's footer. Returns null when meta is empty so the card stays clean. */
export function StageMetaDetails({ meta, fields }) {
    if (!meta || Object.keys(meta).length === 0) return null;
    const rows = Object.entries(meta).filter(([_k, v]) => v !== "" && v !== null && v !== undefined);
    if (rows.length === 0) return null;
    return (
        <details className="mt-2 text-[11px]" data-testid="stage-meta-details">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground font-semibold">
                View captured data ({rows.length})
            </summary>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 pl-2 border-l-2 border-border">
                {rows.map(([k, v]) => {
                    const spec = fields?.[k];
                    const label = spec?.label || k.replace(/_/g, " ");
                    const display = typeof v === "boolean" ? (v ? "✓ Yes" : "—")
                        : Array.isArray(v) ? v.join(", ")
                        : String(v);
                    return (
                        <React.Fragment key={k}>
                            <dt className="text-muted-foreground">{label}</dt>
                            <dd className="font-semibold text-foreground break-words">{display}</dd>
                        </React.Fragment>
                    );
                })}
            </dl>
        </details>
    );
}
